/*
 * A record page on a phone (visit, job, client), flush like Jobber's (owner, 2026-10-30: "more flush and better ui ... we
 * just don't have missing features or things hard to reach"): Back and the client's Call, Text and Email up top, the
 * status, the name, a link line under it, fact rows that do something when tapped (the address opens directions, the time
 * opens Edit schedule), the page's own buttons in one row, then its sections one under the other with no boxes.
 * Like Jobber's visit and job pages (owner shots, 2026-10-30): a kind icon by the status, buttons under a fact (Directions,
 * On my way), tabs under the buttons (Visit, Details, Notes) and a floating button over the tabs bar (Open Camera, +).
 */
import { Fragment, useState, type ReactNode } from "react";
import { DirectionsChoice, openSavedDirections } from "./Directions";
import { Icon, type IconName } from "./Icon";

export interface PhoneFact {
  key: string;
  icon: IconName;
  main: ReactNode;
  sub?: ReactNode;
  /** A link (a phone number, a page) or a tap that opens something. Neither: the row only shows. */
  href?: string;
  /** An address: the tap asks Apple Maps or Google Maps, then opens directions there. */
  directions?: string;
  onClick?: () => void;
  /** Buttons right under this row (Directions and On my way under the address). */
  after?: ReactNode;
}

export interface PhoneTabs {
  tabs: { key: string; label: string }[];
  active: string;
  onChange: (key: string) => void;
}

const digits = (phone: string) => phone.replace(/[^\d+]/g, "");

/** Back goes to the page the person came from; opened straight from a link, it goes to the list instead. */
function goBack(fallback: () => void) {
  const state: unknown = window.history.state;
  const index = state && typeof state === "object" && "__TSR_index" in state ? Number(state.__TSR_index) : 0;
  if (index > 0) window.history.back();
  else fallback();
}

export function PhoneRecord({
  back,
  kind,
  status,
  title,
  sub,
  phone,
  email,
  facts = [],
  meta = [],
  actions,
  tabs,
  fab,
  contactExtra,
  children,
}: {
  back: { label: string; go: () => void };
  /** The record's kind by its status, as Jobber's truck for a visit and hammer for a job. */
  kind?: IconName;
  status?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  phone?: string | null;
  email?: string | null;
  facts?: PhoneFact[];
  /** Short label and value pairs side by side under the title, like Jobber's Issued and Due. */
  meta?: { label: string; value: ReactNode }[];
  actions?: ReactNode;
  tabs?: PhoneTabs;
  /** A floating button over the bottom tabs: Open Camera on a visit, + on a job. */
  fab?: ReactNode;
  /** One more round button in the contact pill (the crew's Message the client, who never see the number). */
  contactExtra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="prec">
      <div className="prec-top">
        <button type="button" className="prec-round" aria-label={back.label} onClick={() => goBack(back.go)}>
          <Icon name="chevronLeft" size={22} />
        </button>
        {phone || email || contactExtra ? (
          <div className="prec-contact">
            {phone ? (
              <a href={`tel:${digits(phone)}`} aria-label="Call">
                <Icon name="phone" size={20} />
              </a>
            ) : null}
            {phone ? (
              <a href={`sms:${digits(phone)}`} aria-label="Text">
                <Icon name="message" size={20} />
              </a>
            ) : null}
            {email ? (
              <a href={`mailto:${email}`} aria-label="Email">
                <Icon name="mail" size={20} />
              </a>
            ) : null}
            {contactExtra}
          </div>
        ) : null}
      </div>
      <header className="prec-head">
        {status ? (
          <div className="prec-status">
            {kind ? <Icon name={kind} size={22} /> : null}
            {status}
          </div>
        ) : null}
        <h1>{title}</h1>
        {sub ? <div className="prec-sub">{sub}</div> : null}
      </header>
      {meta.length ? (
        <dl className="prec-meta">
          {meta.map((m) => (
            <div key={m.label}>
              <dt>{m.label}</dt>
              <dd>{m.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      <PhoneFacts facts={facts} top />
      {actions ? <div className="prec-actions">{actions}</div> : null}
      {tabs ? (
        <div className="prec-tabs" role="tablist">
          {tabs.tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={t.key === tabs.active}
              className={t.key === tabs.active ? "on" : undefined}
              onClick={() => tabs.onChange(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      ) : null}
      <div className="prec-body">{children}</div>
      {fab ? <div className="prec-fab">{fab}</div> : null}
    </div>
  );
}

/** Fact rows that do something when tapped (directions, a call, a page). Also used lower on a page, like Jobber's client row. */
export function PhoneFacts({ facts, top = false }: { facts: PhoneFact[]; top?: boolean }) {
  const [maps, setMaps] = useState<string | null>(null);
  if (!facts.length) return null;
  return (
    <div className={top ? "prec-facts top" : "prec-facts"}>
      <DirectionsChoice address={maps} onClose={() => setMaps(null)} />
      {facts.map((f) => {
        const inner = (
          <>
            <Icon name={f.icon} size={22} />
            <span>
              <b>{f.main}</b>
              {f.sub ? <span className="muted">{f.sub}</span> : null}
            </span>
            {f.href || f.onClick || f.directions ? <Icon name="chevronRight" size={20} /> : null}
          </>
        );
        const row = f.href ? (
          <a
            key={f.key}
            className="prec-fact"
            href={f.href}
            target={f.href.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
          >
            {inner}
          </a>
        ) : f.onClick || f.directions ? (
          <button
            key={f.key}
            type="button"
            className="prec-fact"
            onClick={
              f.directions
                ? () => {
                    if (f.directions && !openSavedDirections(f.directions)) setMaps(f.directions);
                  }
                : f.onClick
            }
          >
            {inner}
          </button>
        ) : (
          <div key={f.key} className="prec-fact">
            {inner}
          </div>
        );
        return f.after ? (
          <Fragment key={f.key}>
            {row}
            <div className="prec-fact-after">{f.after}</div>
          </Fragment>
        ) : (
          row
        );
      })}
    </div>
  );
}
